import { useCallback, useEffect, useMemo, useState } from 'react'
import { useEnv } from '@praxis/component-runtime-env'
import { useBrand } from '../../../shared/brand/context/BrandContext'
import { mapBrandListToCatalog } from '../mappers/brandCatalogMapper'
import {
  mapCreateBrandPayload,
  mapUpdateBrandPayload,
} from '../../brandSetup/mappers/brandRequestMapper'
import { brandSetupService } from '../../brandSetup/services/brandSetupService'
import { brandService } from '../../../shared/brand/services/brandService'

export const useBrandCatalog = ({ enabled = true } = {}) => {
  const { brandCardConfig = {} } = useEnv()
  const { allCategories } = useBrand()
  const [brandResponse, setBrandResponse] = useState(null)
  const [error, setError] = useState(null)
  const [isCreating, setIsCreating] = useState(false)
  const [isLoading, setIsLoading] = useState(enabled)
  const [isUpdating, setIsUpdating] = useState(false)
  const catalog = useMemo(
    () =>
      brandResponse === null
        ? null
        : mapBrandListToCatalog(brandResponse, allCategories, brandCardConfig),
    [allCategories, brandCardConfig, brandResponse],
  )

  const loadBrands = useCallback(async () => {
    setError(null)
    setIsLoading(true)
    try {
      const response = await brandService.getAllBrands()
      setBrandResponse(response)
      return response
    } catch (requestError) {
      setError(requestError)
      throw requestError
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!enabled) {
      setBrandResponse(null)
      setError(null)
      setIsLoading(false)
      return
    }

    loadBrands().catch(() => {})
  }, [enabled, loadBrands])

  const createBrand = useCallback(
    async (form) => {
      setIsCreating(true)
      try {
        const payload = mapCreateBrandPayload(form)
        const response = await brandSetupService.createBrand(payload)
        await loadBrands().catch(() => {})
        return response
      } finally {
        setIsCreating(false)
      }
    },
    [loadBrands],
  )

  const updateBrand = useCallback(
    async (form, originalBrand) => {
      setIsUpdating(true)
      try {
        const payload = mapUpdateBrandPayload(form, originalBrand)
        const brandUuid = originalBrand?.id
        const response = await brandSetupService.updateBrand(brandUuid, payload)
        await loadBrands().catch(() => {})
        return response
      } finally {
        setIsUpdating(false)
      }
    },
    [loadBrands],
  )

  return {
    catalog,
    createBrand,
    error,
    isCreating,
    isLoading,
    isUpdating,
    updateBrand,
  }
}
